/** MI DOMINO - PhilCoin wallet UI: balance display + cosmetic bling.
 *  Reads WALLET (state) and STORE (catalog). Pure leaves.
 *  (saveWallet/earnCoins stay in legacy: saveWallet is runtime-decorated with a
 *  cloud-sync hook, and earnCoins calls it.) */
import { WALLET } from '../state';
import { STORE, iname } from '../data/catalog';
import { FLAG_ART } from './flagArt';

export function updateCoinBal(): void {
  const el = document.getElementById('coin-bal');
  if (el) el.textContent = String(WALLET.coins);
}

/** The ⚙️ "Fichas" select is the ONE list of domino sets: the 4 built-in looks
 *  plus every set bought in la tienda. Its value mirrors WALLET.active, so a set
 *  equipped from the store shows up selected here, and a purchase is never
 *  "missing from the list". A store set also clears the built-in look (both
 *  paint --tile-face; the built-in classes used to win and hide the purchase). */
export function syncSkinSelect(): void {
  const sel = document.getElementById('skin') as HTMLSelectElement | null;
  if (!sel) return;
  const base = [...sel.options].filter((o) => !o.value.startsWith('fich-'));
  const owned = STORE.filter((it) => it.kind === 'fichas' && WALLET.owned.includes(it.id));
  sel.innerHTML = '';
  for (const o of base) sel.appendChild(o);
  for (const it of owned) {
    const o = document.createElement('option');
    o.value = it.id;
    o.textContent = `🁢 ${iname(it)}`;
    sel.appendChild(o);
  }
  const activeSet = owned.find((it) => WALLET.active.includes(it.id));
  if (activeSet) {
    document.body.classList.remove('skin-midnight', 'skin-gold', 'skin-coral');
    try { localStorage.setItem('skin', ''); } catch { /* */ }
    sel.value = activeSet.id;
  } else {
    let sk = '';
    try { sk = localStorage.getItem('skin') || ''; } catch { /* */ }
    sel.value = sk;
  }
}

/** Generic "built-in options + owned store items" select sync (felt / wood
 *  follow the same rule as the domino sets): the select's value mirrors
 *  WALLET.active, bought items appear as extra <option>s. */
function syncStoreSelect(selId: string, kinds: string[], builtinKey: string, builtinClasses: string[]): void {
  const sel = document.getElementById(selId) as HTMLSelectElement | null;
  if (!sel) return;
  const base = [...sel.options].filter((o) => !o.dataset.store);
  const owned = STORE.filter((it) => kinds.includes(it.kind) && WALLET.owned.includes(it.id));
  sel.innerHTML = '';
  for (const o of base) sel.appendChild(o);
  for (const it of owned) {
    const o = document.createElement('option');
    o.value = it.id;
    o.dataset.store = '1';
    o.textContent = `${it.icon || '✨'} ${iname(it)}`;
    sel.appendChild(o);
  }
  const activeItem = owned.find((it) => WALLET.active.includes(it.id));
  if (activeItem) {
    if (builtinClasses.length) document.body.classList.remove(...builtinClasses);
    try { localStorage.setItem(builtinKey, ''); } catch { /* */ }
    sel.value = activeItem.id;
  } else {
    let v = '';
    try { v = localStorage.getItem(builtinKey) || ''; } catch { /* */ }
    sel.value = v;
  }
}

/** ⚙️ Table felt = built-in felts + every flag / designer table you own. */
export function syncFeltSelect(): void {
  syncStoreSelect('felt-select', ['flag', 'feltx'], 'felt', ['felt-wine', 'felt-ocean', 'felt-royal']);
}

/** ⚙️ Table wood = classic + every wood finish you own. */
export function syncWoodSelect(): void {
  syncStoreSelect('wood-select', ['skin'], 'wood', []);
}

/** 🌅 Ambient scene behind the table — pure CSS, free, persisted. */
export const SCENES = ['', 'scene-playa', 'scene-malecon', 'scene-noche', 'scene-patio', 'scene-casino', 'scene-cafe'];
export function applyScene(v?: string): void {
  let scene = v;
  if (scene === undefined) { try { scene = localStorage.getItem('scene') || ''; } catch { scene = ''; } }
  if (!SCENES.includes(scene!)) scene = '';
  document.body.classList.remove(...SCENES.filter(Boolean));
  if (scene) document.body.classList.add(scene);
  try { localStorage.setItem('scene', scene!); } catch { /* */ }
  const sel = document.getElementById('scene-select') as HTMLSelectElement | null;
  if (sel) sel.value = scene!;
}

export function applyBling(): void {
  for (const it of STORE) {
    if (it.kind === 'flag' || it.kind === 'feltx') continue;
    document.body.classList.toggle(it.id, WALLET.active.includes(it.id));
  }
  // flag & designer tables paint the felt itself
  const felt = document.getElementById('felt');
  const af = STORE.find(
    (it) => (it.kind === 'flag' || it.kind === 'feltx') && WALLET.active.includes(it.id),
  );
  if (felt) {
    if (!af) felt.style.background = '';
    else if (af.kind === 'flag' && FLAG_ART[af.id]) {
      /* real flag artwork (flagArt.ts) — no dark overlay, so the colors read true */
      felt.style.background = `url("data:image/svg+xml;utf8,${encodeURIComponent(FLAG_ART[af.id])}") center / cover no-repeat`;
    } else if (af.kind === 'flag') {
      felt.style.background = `linear-gradient(rgba(8,12,8,.38), rgba(8,12,8,.38)), ${af.bg}`;
    } else {
      felt.style.background = af.bg;
    }
  }
}
